import { useAxios } from '@root/utils/axios';
import { withFormProps } from '@root/utils/withForm';
import { useField, useFormikContext } from 'formik';
import { useEffect, useState } from 'react';
import { MacroMethodsDataServerData } from '../Types';
import Content, { MacroContentProps } from './Content';

type MacroContainerProps<T> = Omit<
    MacroContentProps,
    'isLoading' | 'onChooseMethod' | 'amountValue' | 'currentMethod'
> &
    Omit<withFormProps<T>, 'errorMessage' | 'handleForm'> & {
        includeBalanceMethod?: boolean;
        includePreinvoiceMethod?: boolean;
        defaultMethod: number;
        initialValues: T;
        clientCountry?: string | null;
    };

const MacroContainer = <T extends Record<string, unknown>>(
    props: MacroContainerProps<T>,
) => {
    const {
        clientCountry,
        initialValues,
        includeBalanceMethod = false,
        includePreinvoiceMethod = true,
        methods,
        defaultMethod,
        setInitValues,
        prefixFields = '',
        ...rProps
    } = props;
    const { setFieldValue } = useFormikContext();
    const axiosInstance = useAxios();

    const [isLoading, setIsLoading] = useState(false);
    const [methodsData, setMethodsData] = useState(methods);
    const [currentMethod, setCurrentMethod] = useState(defaultMethod);
    const [defMethod, setDefMethod] = useState(defaultMethod);

    const [, { value: amountValue }] = useField(`${prefixFields}amount`);

    /*
    const formSubmit: FormHandlerInside<MacroFormValues> = (ctx) => {
        const responseMiddleWare: FormHandlerInsideResponseMiddleware<
            MacroServerResponse
        > = (response) => {
            if (typeof response.paymentid === 'number') {
                setFieldValue(`${prefixFields}paymentid`, response.paymentid);
            } else {
                setFieldValue(`${prefixFields}paymentid`, undefined);
            }
        };
        return submitHandler({
            ...ctx,
            responseMiddleWare,
        });
    };
    */

    useEffect(() => {
        if (typeof initialValues.payment !== 'undefined') {
            setInitValues({
                ...initialValues,
                payment: {
                    ...(initialValues.payment as Record<string, unknown>),
                    payment: currentMethod,
                },
            });
        } else {
            setInitValues({
                ...initialValues,
                payment: currentMethod,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setIsLoading(true);
        setCurrentMethod(0);
        axiosInstance
            .post<MacroMethodsDataServerData>('/public/macroPay/methods', {
                includeBalanceMethod,
                includePreinvoiceMethod,
                country: clientCountry || null,
            })
            .then(({ data }) => {
                setMethodsData(data.methods);
                setFieldValue(`${prefixFields}payment`, data.defaultMethod);
                setCurrentMethod(data.defaultMethod);
                setDefMethod(data.defaultMethod);
                setIsLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const method = methodsData.find((m) => m.items.find(i => i.value === currentMethod))?.items.find(i => i.value === currentMethod);
        if (
            method &&
            method.min !== null &&
            parseFloat(amountValue || 0) < method.min
        ) {
            setCurrentMethod(defMethod);
            setFieldValue(`${prefixFields}payment`, defMethod);
        } else if (
            method &&
            method.max !== null &&
            parseFloat(amountValue || 0) > method.max
        ) {
            setCurrentMethod(defMethod);
            setFieldValue(`${prefixFields}payment`, defMethod);
        }
    }, [amountValue, currentMethod]);

    return (
        <Content
            {...rProps}
            prefixFields={prefixFields}
            isLoading={isLoading}
            methods={methodsData}
            amountValue={amountValue}
            currentMethod={currentMethod}
            onChooseMethod={(value) => {
                setCurrentMethod(value);
                setFieldValue(`${prefixFields}payment`, value);
            }}
        />
    );
};

export default MacroContainer;
